import { Component, inject, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { StorageService } from 'src/app/services/auth/storage.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { BroadcastUtil } from 'src/app/utils/broadcast.util';
import { LoginResponse } from 'src/app/models';
import { environment } from 'src/environments/environment';

declare const google: any;

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_UNAVAILABLE =
  'Não foi possível iniciar o login com o Google.';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private queryParams = inject(ActivatedRoute).queryParams;

  errorMessage = '';
  redirectUrl: string;
  /** Google login only renders when a client id is configured. */
  readonly googleEnabled = !!environment.googleClientId;

  ngOnInit() {
    this.queryParams.subscribe((params) => {
      if (params['redirectUrl']) this.redirectUrl = params['redirectUrl'];
    });
    if (this.googleEnabled) {
      this.loadGisScript().catch(() => this.showGoogleUnavailable());
    }
  }

  /**
   * Google-only login via the GIS token client with prompt 'select_account':
   * ALWAYS opens Google's account chooser, even when a single session is
   * active or the browser remembers the last account (Jan keeps several
   * Google accounts signed in and must pick the @b4a.com.br one). The chosen
   * account's ACCESS token proves identity server-side (tokeninfo + aud) and
   * feeds the central B4A MCP login check.
   */
  async signInWithGoogle(): Promise<void> {
    try {
      await this.loadGisScript();
    } catch {
      this.showGoogleUnavailable();
      return;
    }
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: environment.googleClientId,
        scope: 'openid email profile',
        prompt: 'select_account',
        hd: 'b4a.com.br',
        callback: (resp: { access_token?: string }) =>
          this.zone.run(() => this.handleGoogleAccessToken(resp?.access_token)),
        error_callback: () =>
          this.zone.run(() => LoaderService.showLoader(false))
      });
      client.requestAccessToken();
    } catch {
      this.showGoogleUnavailable();
    }
  }

  private showGoogleUnavailable(): void {
    AppDialogService.showErrorDialog({}, false, GOOGLE_UNAVAILABLE);
  }

  private async handleGoogleAccessToken(
    accessToken?: string
  ): Promise<void> {
    if (!accessToken) return;
    try {
      LoaderService.showLoader();
      const data = await this.authService.loginWithGoogle(null, accessToken);
      this.completeLogin(data);
    } catch (error) {
      LoaderService.showLoader(false);
      const message =
        (error as { error?: { message?: string } })?.error?.message ||
        'Não foi possível entrar com o Google.';
      AppDialogService.showErrorDialog({}, false, message);
    }
  }

  private completeLogin(data: LoginResponse): void {
    StorageService.saveResult(data);
    BroadcastUtil.get('userChanged').emit();
    if (this.redirectUrl) window.location.href = this.redirectUrl;
    else this.router.navigate(['/']);
  }

  private loadGisScript(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (typeof google !== 'undefined' && google?.accounts?.oauth2) {
        resolve();
        return;
      }
      const existing = document.querySelector(
        `script[src="${GIS_SRC}"]`
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
        return;
      }
      const script = document.createElement('script');
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }
}
